import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { GALLERY } from "../../data";
import { useLang } from "../../i18n";

export const Gallery = () => {
  const { t } = useLang();
  const g = t.gallery;
  const items = GALLERY.map((item, i) => ({ src: item.src, ...g.items[i] }));
  const featured = items[0];
  const rest = items.slice(1);
  const [active, setActive] = useState(null);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-24 sm:py-32 bg-[#060c1f]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionLabel num="[ 07 ]">{g.label}</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-12">
            <h2 className="font-head font-extrabold uppercase tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.9] max-w-2xl">
              {g.title1}
              <span className="block text-gold-grad">{g.title2}</span>
            </h2>
            <p className="text-slate-400 max-w-sm flex items-center gap-2">
              <Camera size={16} className="text-amber-400 shrink-0" />
              {g.intro}
            </p>
          </div>
        </Reveal>

        {/* Featured full-width crew photo */}
        <Reveal>
          <button
            data-testid="gallery-item-0"
            onClick={() => setActive(featured)}
            className="group relative w-full overflow-hidden rounded-2xl border border-white/10 mb-4 block"
          >
            <div className="h-[280px] sm:h-[440px] overflow-hidden">
              <img
                src={featured.src}
                alt={featured.caption}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#060c1f] via-[#060c1f]/20 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6 sm:p-8 text-left">
              <span className="font-mono-x text-[10px] uppercase tracking-[0.25em] text-amber-400">{featured.tag}</span>
              <div className="font-head font-extrabold uppercase tracking-tight text-white text-2xl sm:text-4xl leading-none mt-2">
                {featured.caption}
              </div>
            </div>
          </button>
        </Reveal>

        {/* Masonry of the rest */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
          {rest.map((item, idx) => {
            const i = idx + 1;
            return (
              <motion.button
                key={i}
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(item)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (idx % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="group relative mb-4 block w-full break-inside-avoid overflow-hidden rounded-xl border border-white/10"
              >
                <img
                  src={item.src}
                  alt={item.caption}
                  loading="lazy"
                  className="w-full h-auto object-cover grayscale-[0.3] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060c1f] via-transparent to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-left translate-y-1 group-hover:translate-y-0 transition-transform">
                  <span className="font-mono-x text-[9px] uppercase tracking-[0.2em] text-amber-400">{item.tag}</span>
                  <div className="font-head font-bold uppercase tracking-wide text-white text-sm sm:text-base leading-tight">
                    {item.caption}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-10 bg-[#040814]/90 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-5 right-5 h-11 w-11 rounded-full border border-white/15 text-white flex items-center justify-center hover:border-amber-400 hover:text-amber-400 transition-colors"
              onClick={() => setActive(null)}
              data-testid="gallery-close"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <motion.figure
              className="max-w-4xl w-full"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.src} alt={active.caption} className="w-full max-h-[75vh] object-contain rounded-xl border border-white/10" />
              <figcaption className="mt-4 flex items-center gap-3">
                <span className="font-mono-x text-[10px] uppercase tracking-[0.2em] text-amber-400 border border-amber-400/30 rounded-full px-3 py-1">
                  {active.tag}
                </span>
                <span className="font-head font-bold uppercase tracking-wide text-white text-lg">{active.caption}</span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
